/**
 * Skills Assessment Service for trAInedup.ai
 * Handles communication with Firebase Cloud Functions for skills assessment and learning plan generation
 */

import { auth, db, functions } from './firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export type InputType = 'single_select' | 'multi_select' | 'free_text';

export interface AssistantMessage {
  role: 'assistant';
  text: string;
  inputType: InputType;
  options?: string[];
  key: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  text?: string;
  timestamp?: any;
  options?: string[];
  inputType?: InputType;
  key?: string;
  id?: string;
}

export interface AssessmentResponse {
  response: string;
  lastAssistantMessage: AssistantMessage;
  conversationHistory: ChatMessage[];
  isAssessmentComplete: boolean;
}

export interface SkillData {
  skill: string;
  domain: 'programming' | 'framework' | 'concept';
  proficiency: number;
  confidence: number;
}

export interface LearningGoal {
  goalType: string;
  description: string;
}

export interface AssessmentResult {
  assessmentId: string;
  extractedData: {
    currentRole: string;
    industry: string;
    aiExperienceLevel: number;
    priorAiTools?: string[];
    needsLanguageSupport?: boolean;
    programmingLanguages: Array<{ language: string; proficiency: number }>;
    aiFrameworks: Array<{ framework: string; proficiency: number }>;
    aiConcepts: Array<{ concept: string; proficiency: number }>;
    learningGoals: LearningGoal[];
    timeCommitmentWeeklyHours: number;
    identifiedKnowledgeGaps: string[];
  };
}

export interface Milestone {
  milestoneTitle: string;
  description: string;
  targetDate: any;
  isCompleted: boolean;
}

export interface RecommendedCourse {
  courseId: string;
  courseTitle: string;
  estimatedCompletionWeeks: number;
  reasoning: string;
  description?: string;
  milestones: Milestone[];
  matchReasons?: string[]; // Array of personalized reasons from GPT-4
  matchScore?: number; // Semantic matching score
  status?: 'active' | 'reserve' | 'disliked'; // Course status in learning plan
  recommendationRank?: number; // Ranking position (1-15)
  priority?: string; // Priority level
}

export interface LearningPlan {
  planId: string;
  userId: string;
  generationDate: any;
  durationMonths: number;
  durationWeeks?: number;
  startDate: any;
  endDate: any;
  status: string;
  overallSummary: string;
  recommendedCourses: RecommendedCourse[];
}

class SkillsAssessmentService {
  /**
   * Send a message to the skills assessment chatbot
   */
  async sendChatMessage(message: string, conversationHistory: ChatMessage[] = []): Promise<AssessmentResponse> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const skillsAssessmentChat = httpsCallable(functions, 'skillsAssessmentChat');
      const result = await skillsAssessmentChat({
        message,
        conversationHistory,
      });

      return result.data as AssessmentResponse;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  }

  /**
   * Process the completed skills assessment
   */
  async processAssessment(): Promise<AssessmentResult> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const processSkillsAssessment = httpsCallable(functions, 'processSkillsAssessment');
      const result = await processSkillsAssessment({});

      const data = result.data as { assessmentId: string; extractedData: any; };
      return {
        assessmentId: data.assessmentId,
        extractedData: data.extractedData,
      };
    } catch (error) {
      console.error('Error processing assessment:', error);
      throw error;
    }
  }

  /**
   * Generate a personalized learning plan
   */
  async generateLearningPlan(assessmentId?: string): Promise<LearningPlan> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const generateLearningPathIntelligent = httpsCallable(functions, 'generateLearningPathIntelligent');
      const result = await generateLearningPathIntelligent({
        assessmentId,
      });

      return result.data as LearningPlan;
    } catch (error) {
      console.error('Error generating learning plan:', error);
      throw error;
    }
  }

  /**
   * Get a user's learning plan
   */
  async getLearningPlan(planId?: string): Promise<LearningPlan> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const planFromFirestore = await this.getLearningPlanFromFirestore(user.uid, planId);
      if (planFromFirestore) {
        return planFromFirestore;
      }

      const callable = httpsCallable(functions, 'getLearningPlan');
      const result = await callable({ planId });
      const responseData = result.data as any;
      return responseData.learningPlan as LearningPlan;
    } catch (error) {
      console.error('Error getting learning plan:', error);
      throw error;
    }
  }

  private async getLearningPlanFromFirestore(
    userId: string,
    planId?: string
  ): Promise<LearningPlan | null> {
    try {
      const resolvedPlanId = await this.resolveLearningPlanId(userId, planId);
      if (!resolvedPlanId) {
        return null;
      }

      const planDoc = await getDoc(doc(db, 'learningPlans', resolvedPlanId));
      if (!planDoc.exists()) {
        return null;
      }

      const planData = planDoc.data() as LearningPlan;
      return {
        ...planData,
        planId: planData.planId ?? resolvedPlanId,
      };
    } catch (firestoreError) {
      console.warn('Falling back to callable learning plan fetch:', firestoreError);
      return null;
    }
  }

  private async resolveLearningPlanId(userId: string, planId?: string): Promise<string | null> {
    if (planId) {
      return planId;
    }

    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data() as { currentLearningPlanId?: string };
    return userData.currentLearningPlanId ?? null;
  }

  /**
   * Start a new skills assessment
   */
  async startAssessment(): Promise<AssessmentResponse> {
    return this.sendChatMessage("Hello, I'd like to start a skills assessment.", []);
  }

  /**
   * Dislike a course and replace it with the next best match
   */
  async dislikeCourseAndReplace(courseId: string, planId: string): Promise<{
    success: boolean;
    replacementCourse: {
      courseId: string;
      title: string;
      category: string;
      matchScore: number;
      matchReasons: string[];
    } | null;
    message: string;
  }> {
    const dislikeCourseFunction = httpsCallable(functions, 'dislikeCourseAndReplace');
    const result = await dislikeCourseFunction({ courseId, planId });
    return result.data as any;
  }
}

// Export singleton instance
export const skillsAssessmentService = new SkillsAssessmentService();
export default skillsAssessmentService;

